import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getStoryDetail,
  listStoryRevisions,
  listNotifyLog,
  listMatchEvents,
  reembedStory,
  cancelEmbedRetries,
  getEmbedStatus,
  recomputeRefinementSession,
  failRefinementSession,
  escalateRefinementSession,
  cancelRefinementSession,
} from '../api/client';
import type {
  AdminStoryDetail,
  StoryRevision,
  NotifyLogEntry,
  RefinementSession,
  EmbedStatus,
} from '../api/types';
import PebbleEnvelopeView from '../components/envelope/PebbleEnvelopeView';
import RevisionDiff from '../components/refinement/RevisionDiff';
import NotifyLogTable from '../components/notify/NotifyLogTable';
import MatchEventsTable from '../components/matchevents/MatchEventsTable';
import { conversationPath } from '../components/matchevents/model';
import type { MatchEventsLog } from '../components/matchevents/model';

export default function StoryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<AdminStoryDetail | null>(null);
  const [revisions, setRevisions] = useState<StoryRevision[]>([]);
  const [notifyEntries, setNotifyEntries] = useState<NotifyLogEntry[]>([]);
  const [matchEvents, setMatchEvents] = useState<MatchEventsLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reembedding, setReembedding] = useState(false);
  const [cancellingEmbed, setCancellingEmbed] = useState(false);

  const pebbleId = detail?.pebble_id ?? null;

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    Promise.all([
      getStoryDetail(id),
      listStoryRevisions(id),
    ])
      .then(([d, r]) => {
        setDetail(d);
        setRevisions(r.revisions);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [id]);

  // Pull doorbell log once we know the pebble id (after detail loads).
  useEffect(() => {
    if (!pebbleId) return;
    listNotifyLog(pebbleId, 20)
      .then((res) => setNotifyEntries(res.entries))
      .catch((e) => console.error('[notify_log]', e));
  }, [pebbleId]);

  // PD-4: the split plane's doorbell. Loaded alongside — never instead of —
  // notify_log: a legacy-sourced pebble rings the old doorbell and writes no
  // node (PB-8), so dropping either view blinds the page for half the pebbles.
  const loadMatchEvents = useCallback(() => {
    if (!pebbleId) return;
    listMatchEvents(pebbleId, 50)
      .then(setMatchEvents)
      .catch((e) => console.error('[match_events]', e));
  }, [pebbleId]);

  useEffect(loadMatchEvents, [loadMatchEvents]);

  async function refreshEmbedStatus(): Promise<void> {
    if (!id || !detail) return;
    try {
      const fresh = await getEmbedStatus(id);
      setDetail({ ...detail, embed_status: fresh });
    } catch (e) {
      console.error('[embed-status refresh]', e);
    }
  }

  async function handleReembed() {
    if (!id) return;
    if (!confirm('Re-embed this story now?')) return;
    setReembedding(true);
    try {
      await reembedStory(id);
      await refreshEmbedStatus();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to re-embed');
    } finally {
      setReembedding(false);
    }
  }

  async function handleCancelEmbed() {
    if (!id) return;
    if (!confirm('Cancel pending embed retries for this story? Any scheduled DO alarm will be cleared.')) return;
    setCancellingEmbed(true);
    try {
      await cancelEmbedRetries(id);
      await refreshEmbedStatus();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to cancel embed retries');
    } finally {
      setCancellingEmbed(false);
    }
  }

  async function handleSessionAction(
    sessionId: string,
    action: 'recompute' | 'fail' | 'escalate' | 'cancel',
  ) {
    const messages = {
      recompute: 'Recompute this session?',
      fail: 'Force-fail this session? Both pebbles will get a doorbell.',
      escalate: 'Bump this session to consent? Both pebbles will get a doorbell.',
      cancel: 'Cancel this session? No doorbell will be sent.',
    };
    if (!confirm(messages[action])) return;
    try {
      const fn = {
        recompute: recomputeRefinementSession,
        fail: failRefinementSession,
        escalate: escalateRefinementSession,
        cancel: cancelRefinementSession,
      }[action];
      const result = await fn(sessionId);
      // Update the session in detail.active_refinement_sessions in place.
      if (result.session && detail) {
        setDetail({
          ...detail,
          active_refinement_sessions: detail.active_refinement_sessions
            .map((s) => (s.id === sessionId ? result.session! : s))
            .filter(
              (s) =>
                s.status !== 'failed' &&
                s.status !== 'escalated_consent' &&
                s.status !== 'cancelled',
            ),
        });
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Action failed');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading story…</div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Error: {error ?? 'Story not found'}
      </div>
    );
  }

  const story = detail.story;

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link to="/stories" className="text-sm text-pebbl-600 hover:text-pebbl-800">
        ← Back to stories
      </Link>

      {/* Header */}
      <div className="card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Story</h1>
            <div className="mt-1 flex items-center gap-3 flex-wrap text-sm text-gray-600">
              <code className="text-xs">{story.id}</code>
              {pebbleId && (
                <>
                  <span className="text-gray-300">·</span>
                  <span className="text-xs">pebble <code className="text-xs">{pebbleId}</code></span>
                </>
              )}
              {detail.user_id && (
                <>
                  <span className="text-gray-300">·</span>
                  <span className="text-xs">user <code className="text-xs">{detail.user_id}</code></span>
                </>
              )}
              <span className="text-gray-300">·</span>
              <span className="text-xs">{new Date(story.createdAt).toLocaleString()}</span>
            </div>
            {/* PD-4's reverse direction: from a story to the conversation that
                produced it (PD-2) and to that pebble's match-events log. */}
            {pebbleId && (
              <div className="mt-2 flex items-center gap-3 text-xs" data-testid="story-split-links">
                <Link to={conversationPath(pebbleId)} className="text-pebbl-600 hover:text-pebbl-800">
                  conversation timeline →
                </Link>
                <Link to={`/match-events/${encodeURIComponent(pebbleId)}`} className="text-pebbl-600 hover:text-pebbl-800">
                  match events →
                </Link>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <EmbedStatusBadge status={detail.embed_status} />
            <a
              href="#revisions"
              className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded hover:bg-gray-200"
            >
              rev {detail.revision_count}
            </a>
            <button
              type="button"
              onClick={handleReembed}
              disabled={reembedding}
              className="px-3 py-1.5 text-sm bg-pebbl-600 text-white rounded-md hover:bg-pebbl-700 disabled:opacity-50"
            >
              {reembedding ? 'Re-embedding…' : 'Re-embed now'}
            </button>
          </div>
        </div>
      </div>

      {/* 1. Pebble envelope */}
      {pebbleId && <PebbleEnvelopeView pebbleId={pebbleId} />}

      {/* 2. Embed status panel — backed by 07-DO-ALARM */}
      <EmbedStatusPanel
        status={detail.embed_status}
        cancelling={cancellingEmbed}
        onCancel={handleCancelEmbed}
      />

      {/* 3. Active refinement sessions */}
      <div className="card">
        <h3 className="font-semibold text-gray-700 mb-2">Active refinement sessions</h3>
        {detail.active_refinement_sessions.length === 0 ? (
          <div className="text-sm text-gray-500">No active refinement sessions.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4">Session</th>
                  <th className="py-2 pr-4">Side</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Attempt</th>
                  <th className="py-2 pr-4">Last score</th>
                  <th className="py-2 pr-4">Updated</th>
                  <th className="py-2 pr-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {detail.active_refinement_sessions.map((s) => (
                  <SessionRow
                    key={s.id}
                    session={s}
                    storyId={story.id}
                    onAction={(a) => handleSessionAction(s.id, a)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 4. Story revisions */}
      <div id="revisions" className="space-y-3">
        <h3 className="font-semibold text-gray-700">Story revisions</h3>
        {revisions.length === 0 ? (
          <div className="card text-sm text-gray-500">No revisions yet.</div>
        ) : (
          revisions.map((rev, idx) => {
            const previous = revisions[idx + 1] ?? null; // newest-first
            return (
              <RevisionDiff
                key={rev.id}
                current={rev}
                previous={previous}
                defaultExpanded={idx < 2}
              />
            );
          })
        )}
      </div>

      {/* 5. Parsed story */}
      <div className="card">
        <h3 className="font-semibold text-gray-700 mb-2">Parsed story</h3>
        <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-x-auto">
          {JSON.stringify(story, null, 2)}
        </pre>
      </div>

      {/* 6. Matches */}
      <div className="card">
        <h3 className="font-semibold text-gray-700 mb-2">Matches</h3>
        {detail.matches.length === 0 ? (
          <div className="text-sm text-gray-500">No matches yet.</div>
        ) : (
          <div className="space-y-2">
            {detail.matches.map((m) => (
              <Link
                key={m.id}
                to={`/matches/${m.id}`}
                className="block border border-gray-200 rounded p-2 hover:bg-gray-50"
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <div className="font-mono text-xs text-gray-600">{m.id}</div>
                    <div className="text-gray-700 line-clamp-1">
                      {m.storyB.snippet || m.storyA.snippet}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold">
                      {((m.scores.final ?? m.scores.overall) * 100).toFixed(0)}%
                    </div>
                    <div className="text-xs text-gray-500">{m.status}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* 7. Match events — the split plane's doorbell (PD-4) */}
      <div>
        <h3 className="font-semibold text-gray-700 mb-2">Match events</h3>
        <MatchEventsTable
          log={matchEvents}
          onAfterReplay={loadMatchEvents}
          emptyLabel={pebbleId ? 'No match events for this pebble.' : 'This story has no pebble.'}
        />
      </div>

      {/* 8. Doorbell log (legacy plane) */}
      <div>
        <h3 className="font-semibold text-gray-700 mb-2">Doorbell log (legacy)</h3>
        <NotifyLogTable
          entries={notifyEntries}
          onAfterReplay={(entry) =>
            setNotifyEntries((prev) => prev.map((e) => (e.id === entry.id ? entry : e)))
          }
        />
      </div>
    </div>
  );
}

function EmbedStatusBadge({ status }: { status: EmbedStatus }) {
  if (status.ready) {
    return (
      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
        embed ready
      </span>
    );
  }
  if (status.embed_failure_reason) {
    return (
      <span
        className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded"
        title={status.embed_failure_reason}
      >
        embed failed
      </span>
    );
  }
  const counter =
    status.retry_count != null ? `${status.retry_count}/4` : '…';
  return (
    <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">
      embed pending {counter}
    </span>
  );
}

/**
 * Detail-page embed-status panel. Shows the full DO-backed retry timeline
 * (retry count, last attempt, next attempt, transient error) plus the
 * persistent failure reason (if any), and exposes the Cancel button when
 * an alarm is currently scheduled.
 */
function EmbedStatusPanel({
  status,
  cancelling,
  onCancel,
}: {
  status: EmbedStatus;
  cancelling: boolean;
  onCancel: () => void;
}) {
  const isRetrying = !status.ready && status.retry_count != null;
  const isFailed = !!status.embed_failure_reason;
  const isPendingFreshSchedule =
    !status.ready && status.retry_count == null && !isFailed;
  const cancelEnabled = isRetrying || isFailed;

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-gray-700">Embed status</h3>
        <EmbedStatusBadge status={status} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div>
          <div className="text-xs text-gray-500">Ready</div>
          <div>{status.ready ? 'yes' : 'no'}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Retry count</div>
          <div>{status.retry_count != null ? `${status.retry_count}/4` : '—'}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Last attempt at</div>
          <div>
            {status.last_attempt_at
              ? new Date(status.last_attempt_at).toLocaleString()
              : '—'}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Next retry at</div>
          <div>
            {status.next_retry_at
              ? new Date(status.next_retry_at).toLocaleString()
              : '—'}
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="text-xs text-gray-500">Failure reason</div>
          <div className="text-red-700 text-sm">
            {status.embed_failure_reason ?? '—'}
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="text-xs text-gray-500">Last DO error</div>
          <div className="text-red-600 text-xs">{status.last_error ?? '—'}</div>
        </div>
      </div>
      {cancelEnabled && (
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={cancelling}
            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {cancelling ? 'Cancelling…' : 'Cancel embed retries'}
          </button>
          <span className="text-xs text-gray-500">
            {isFailed
              ? 'Clears the embed_failure_reason marker so the story can be retried.'
              : 'Cancels the pending DO alarm chain immediately.'}
          </span>
        </div>
      )}
      {isPendingFreshSchedule && (
        <div className="mt-3 text-xs text-gray-500 italic">
          Pipeline reported vectors not yet visible — DO alarm may not have
          been scheduled yet, or this story has no pebble id.
        </div>
      )}
    </div>
  );
}

function SessionRow({
  session,
  storyId,
  onAction,
}: {
  session: RefinementSession;
  storyId: string;
  onAction: (action: 'recompute' | 'fail' | 'escalate' | 'cancel') => void;
}) {
  const side: 'a' | 'b' = session.storyAId === storyId ? 'a' : 'b';
  return (
    <tr className="border-b border-gray-100">
      <td className="py-2 pr-4">
        <Link
          to={`/matches/${session.matchId}?tab=refinement`}
          className="text-pebbl-600 hover:text-pebbl-800 font-mono text-xs"
        >
          {session.id.slice(0, 12)}…
        </Link>
      </td>
      <td className="py-2 pr-4 uppercase text-xs">{side}</td>
      <td className="py-2 pr-4">
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
          {session.status}
        </span>
      </td>
      <td className="py-2 pr-4">
        {session.attempt}/{session.maxAttempts}
      </td>
      <td className="py-2 pr-4">
        {session.lastScore != null ? session.lastScore.toFixed(3) : '—'}
      </td>
      <td className="py-2 pr-4 text-xs">
        {new Date(session.updatedAt).toLocaleString()}
      </td>
      <td className="py-2 pr-4">
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => onAction('recompute')}
            className="px-2 py-0.5 text-xs rounded bg-pebbl-600 text-white hover:bg-pebbl-700"
          >
            Recompute
          </button>
          <button
            type="button"
            onClick={() => onAction('fail')}
            className="px-2 py-0.5 text-xs rounded bg-red-600 text-white hover:bg-red-700"
          >
            Fail
          </button>
          <button
            type="button"
            onClick={() => onAction('escalate')}
            className="px-2 py-0.5 text-xs rounded bg-green-600 text-white hover:bg-green-700"
          >
            Escalate
          </button>
          <button
            type="button"
            onClick={() => onAction('cancel')}
            className="px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-800 hover:bg-gray-300"
          >
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
}
